import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { getOrCreatePersistentCacheItem } from "../storage/Storage";

export type CallbackHttpWithObject = (
  url: string,
  input?: any,
  enableLocalCache?: boolean
) => Promise<AxiosResponse<any, any>>;

/** POST with optional per-request axios config (e.g. timeout). */
export type PostCallback = (
  url: string,
  input?: any,
  config?: AxiosRequestConfig
) => Promise<AxiosResponse<any, any>>;

export type RequestApi = {
  fetch: any;
  get: CallbackHttpWithObject;
  post: PostCallback;
  postBlob: (url: string, input?: any) => Promise<AxiosResponse<Blob, any>>;
  put: CallbackHttpWithObject;
  patch: CallbackHttpWithObject;
  delete: CallbackHttpWithObject;
};

export function buildRequestApi(accessToken: string): RequestApi {
  let headers = {};
  if (accessToken) {
    headers = {
      Authorization: `${accessToken}`,
    };
  }
  const defaultOptions: { headers: any; data?: unknown } = { headers };

  const _fetch = (url: string, headers?: any, config?: any) => {
    let options: any = {};
    if (headers) {
      options = { headers };
    } else {
      options = defaultOptions;
    }
    if (config) {
      options = { ...options, ...config };
    }
    return fetch(url, options);
  };

  const _get = (url: string, input?: any, enableLocalCache?: boolean) => {
    let promiseResult: Promise<AxiosResponse<any, any>>;
    if (enableLocalCache) {
      const cachedResult = getOrCreatePersistentCacheItem(url);
      if (cachedResult) {
        return Promise.resolve(cachedResult);
      }
    }
    if (input) {
      const params = new URLSearchParams(input).toString();
      promiseResult = axios.get(`${url}?${params}`, defaultOptions);
    } else {
      promiseResult = axios.get(url, defaultOptions);
    }
    if (!enableLocalCache) return promiseResult;

    return promiseResult.then((response) => {
      getOrCreatePersistentCacheItem(url, response);
      return response;
    });
  };

  const _post = (url: string, input: any, config?: AxiosRequestConfig) => {
    return axios.post(url, input, { ...defaultOptions, ...config });
  };
  const _postBlob = (url: string, input: any) => {
    return axios.post<Blob>(url, input, {
      ...defaultOptions,
      responseType: "blob",
    });
  };
  const _put = (url: string, input: any) => {
    return axios.put(url, input, defaultOptions);
  };
  const _patch = (url: string, input: any) => {
    return axios.patch(url, input, defaultOptions);
  };
  const _delete = (url: string, input?: any) => {
    if (input) {
      defaultOptions.data = input;
    }
    return axios.delete(url, defaultOptions);
  };

  return {
    fetch: _fetch,
    get: _get,
    post: _post,
    postBlob: _postBlob,
    put: _put,
    patch: _patch,
    delete: _delete,
  };
}
